import React, { useMemo, useState } from 'react';
import { GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import AddHomeIcon from '@mui/icons-material/AddHome';
import AddIcon from '@mui/icons-material/Add';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import KeyIcon from '@mui/icons-material/Key';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveIcon from '@mui/icons-material/Remove';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import StickyNote2Icon from '@mui/icons-material/StickyNote2';
import UpgradeIcon from '@mui/icons-material/Upgrade';

import { Device } from '../interfaces';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    ROLES_WITH_DEVICE_ATTACH_ORGANIZATION,
    ROLES_WITH_DEVICE_CHANGE_VERSION,
    ROLES_WITH_DEVICE_CREATE,
    ROLES_WITH_DEVICE_EXPORT_IDS,
    ROLES_WITH_DEVICE_GET_SECRETS,
    ROLES_WITH_DEVICE_REMOVE,
    ROLES_WITH_DEVICE_RESET_INSTALL,
    ROLES_WITH_DEVICE_UPLOAD_LOGS,
    ROLES_WITH_HARDWARE_CONFIG_ACCESS,
    ROLES_WITH_ROUTE_CARD_ACCESS,
} from '../permissions';
import { BatchCompleteOperationsPopover } from './BatchCompleteOperationsPopover';
import { selectSelectedOnboardDeviceTableRows } from '../store/devices.reducer';
import { loadActivationSticks } from '../store/activationSticks.reducer';
import { loadOnboardDevicesHardwareConfigurations } from '../store/onboardDeviceHardwareConfigurations.reducer';
import { loadOnboardDevicesOnlinePeers } from '../store/onboardDevicesOnlinePeers.reducer';
import { loadSystemConfigurations } from '../store/systemConfigurations.reducer';
import { useCognitoUser } from '../hooks/useCognitoUser';
import { hasAllowedRole } from '../utils/hasAllowedRole';
import { exportToFile, exportToPdf, getSelectedDevices } from '../utils/onboardDevicesExport';
import { AttachOrganizationDialog } from './AttachOrganizationDialog';
import { CreateDevicesDialog } from './CreateDevicesDialog';
import { GetSecretsDialog } from './GetSecretsDialog';
import { RemoveDevicesDialog } from './RemoveDevicesDialog';
import { UpdateVersionDialog } from './UpdateVersionDialog';
import { ResetInstallDialog } from './ResetInstallDialog';
import { UploadOnboardDeviceLogsDialog } from './UploadOnboardDeviceLogsDialog';

interface OnboardDevicesToolbarProps {
    devices: Device[];
    hasScannedFilter: boolean;
    scannedDevicesCount: number;
    onResetScannedDevices: () => void;
    onReloadOnboardDevices: () => void;
}

type OnboardDevicesDialog =
    | 'updateVersion'
    | 'getSecrets'
    | 'attachOrganization'
    | 'createDevices'
    | 'removeDevices'
    | 'uploadLogs'
    | 'resetInstall';

export function OnboardDevicesToolbar({
    devices,
    hasScannedFilter,
    scannedDevicesCount,
    onResetScannedDevices,
    onReloadOnboardDevices,
}: OnboardDevicesToolbarProps) {
    const dispatch = useAppDispatch();
    const { userRoles } = useCognitoUser();
    const selectedRows = useAppSelector(selectSelectedOnboardDeviceTableRows);
    const [activeDialog, setActiveDialog] = useState<OnboardDevicesDialog | null>(null);

    const canResetInstallRole = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_RESET_INSTALL);
    }, [userRoles]);

    const canChangeVersion = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_CHANGE_VERSION);
    }, [userRoles]);

    const canAttachOrganization = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_ATTACH_ORGANIZATION);
    }, [userRoles]);

    const canCreateDevices = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_CREATE);
    }, [userRoles]);

    const canRemoveDevices = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_REMOVE);
    }, [userRoles]);

    const canGetSecrets = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_GET_SECRETS);
    }, [userRoles]);

    const canExportIds = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_EXPORT_IDS);
    }, [userRoles]);

    const canUploadLogs = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_DEVICE_UPLOAD_LOGS);
    }, [userRoles]);

    const canAccessRouteCards = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_ROUTE_CARD_ACCESS);
    }, [userRoles]);

    const canViewHardwareConfigurations = useMemo(() => {
        return userRoles && hasAllowedRole(userRoles, ROLES_WITH_HARDWARE_CONFIG_ACCESS);
    }, [userRoles]);

    const RESET_INSTALL_ALLOWED_STATUSES = new Set([
        'InProgressPhase1',
        'InProgressPhase1Done',
        'InProgressPhase2',
        'InProgressPhase2Done',
        'Defect',
    ]);

    const canResetInstall = useMemo(() => {
        if (selectedRows.length !== 1) return false;
        const device = devices.find((d) => d.deviceId === selectedRows[0]);
        return !!device && RESET_INSTALL_ALLOWED_STATUSES.has(device.status ?? '');
    }, [selectedRows, devices]);

    const onReload = () => {
        onReloadOnboardDevices();
        dispatch(loadActivationSticks());
        dispatch(loadSystemConfigurations());

        // Not every role that can reach this page may read hardware configurations.
        if (canViewHardwareConfigurations) {
            dispatch(loadOnboardDevicesHardwareConfigurations());
        }

        dispatch(loadOnboardDevicesOnlinePeers());
    };

    const closeDialog = () => setActiveDialog(null);

    const closeDialogAndReload = () => {
        closeDialog();
        onReloadOnboardDevices();
    };

    const onExportIds = () => {
        const selectedDevices = getSelectedDevices(selectedRows, devices);

        exportToFile(selectedDevices);
        exportToPdf(selectedDevices);
    };

    return (
        <>
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <Button
                    startIcon={<FilterAltOffIcon />}
                    disabled={!hasScannedFilter}
                    color="primary"
                    onClick={onResetScannedDevices}
                >
                    {hasScannedFilter
                        ? `Clear Scanned Devices (${scannedDevicesCount})`
                        : 'Clear Scanned Devices'}
                </Button>
                {canChangeVersion && (
                    <Button
                        startIcon={<UpgradeIcon />}
                        disabled={selectedRows.length === 0}
                        color="primary"
                        onClick={() => setActiveDialog('updateVersion')}
                    >
                        Change Version
                    </Button>
                )}
                {canAccessRouteCards && (
                    <BatchCompleteOperationsPopover
                        selectedDeviceIds={selectedRows}
                        onComplete={closeDialogAndReload}
                    />
                )}
                <Button startIcon={<RefreshIcon />} color="primary" onClick={onReload}>
                    Reload
                </Button>
                {canGetSecrets && (
                    <Button
                        disabled={selectedRows.length !== 1}
                        startIcon={<KeyIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('getSecrets')}
                    >
                        Get Secrets
                    </Button>
                )}
                {canAttachOrganization && (
                    <Button
                        disabled={selectedRows.length === 0}
                        startIcon={<AddHomeIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('attachOrganization')}
                    >
                        Attach Organization
                    </Button>
                )}
                {canCreateDevices && (
                    <Button
                        startIcon={<AddIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('createDevices')}
                    >
                        Create Devices
                    </Button>
                )}
                {canRemoveDevices && (
                    <Button
                        disabled={selectedRows.length === 0}
                        startIcon={<RemoveIcon />}
                        color="primary"
                        onClick={() => setActiveDialog('removeDevices')}
                    >
                        Remove Devices
                    </Button>
                )}
                {canExportIds && (
                    <Button
                        startIcon={<AddIcon />}
                        color="primary"
                        disabled={selectedRows.length === 0}
                        onClick={onExportIds}
                    >
                        Export Ids
                    </Button>
                )}
                {canUploadLogs && (
                    <Button
                        startIcon={<StickyNote2Icon />}
                        color="primary"
                        disabled={selectedRows.length !== 1}
                        onClick={() => setActiveDialog('uploadLogs')}
                    >
                        Upload Logs
                    </Button>
                )}
                {canResetInstallRole && (
                    <Button
                        startIcon={<RestartAltIcon />}
                        color="error"
                        disabled={!canResetInstall}
                        onClick={() => setActiveDialog('resetInstall')}
                    >
                        Reset Install
                    </Button>
                )}
            </GridToolbarContainer>

            {activeDialog === 'updateVersion' && (
                <UpdateVersionDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onUpdateComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'getSecrets' && (
                <GetSecretsDialog selectedDeviceId={selectedRows[0]} open onClose={closeDialog} />
            )}
            {activeDialog === 'attachOrganization' && (
                <AttachOrganizationDialog
                    selectedDeviceIds={selectedRows}
                    open
                    onClose={closeDialog}
                    onAttachComplete={closeDialogAndReload}
                />
            )}
            {activeDialog === 'createDevices' && (
                <CreateDevicesDialog open onClose={closeDialog} onCreationCompleted={closeDialogAndReload} />
            )}
            {activeDialog === 'removeDevices' && (
                <RemoveDevicesDialog
                    open
                    selectedDeviceIds={selectedRows}
                    onClose={closeDialog}
                    onRemovalCompleted={closeDialogAndReload}
                />
            )}
            {activeDialog === 'uploadLogs' && (
                <UploadOnboardDeviceLogsDialog
                    selectedDeviceId={selectedRows[0]}
                    open
                    onClose={closeDialog}
                />
            )}
            {activeDialog === 'resetInstall' && (
                <ResetInstallDialog
                    selectedDeviceId={selectedRows[0]}
                    open
                    onClose={closeDialog}
                    onResetCompleted={closeDialogAndReload}
                />
            )}
        </>
    );
}
